import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, FileText, ChevronLeft, ChevronRight, Search, Sparkles } from 'lucide-react';
import { InteractiveReport } from '../reportView';
import MapView from '../reportView/MapView';
import ReportErrorBoundary from './ReportErrorBoundary';
import { apiService } from '../services/api';
import type { FrameCardPayload, FrameCatalogResponse, MicroReport, ReportSectionsResponse } from '../services/api';
import { CustomReportEditor } from '../customReports';
import { exportWorkspacePdfZip, getExportableReports, WorkspaceExportProgress } from '../utils/exportWorkspace';

export type InsightFrame = {
  id: string;
  frameNumber: number;
  name: string;
  summary: string;
  fullHtml: string;
  descriptionHtml?: string;
  source?: string;
  rawDetails?: Record<string, unknown>;
};

type FrameSourceMode = 'none' | 'markdown' | 'catalog';

type FrameSourceState = {
  mode: FrameSourceMode;
  frames: InsightFrame[];
  selector: InsightFrame[];
  generator: InsightFrame[];
};

type CanvasTableMeta = { id: number; rows: number; cols: number; label: string; rowTitles: string[]; colTitles: string[] };

declare global {
  interface Window {
    __canvasTables?: CanvasTableMeta[];
  }
}

interface DocumentViewerProps {
  report: MicroReport | null;
  onAddToScratchpad: (item: any, category?: string) => void;
  isBusy?: boolean;
  analysisQueuePending?: boolean;
  categories?: string[];
  onAddCategory?: (name: string) => void;
  selectedFrames?: number[];
  completedFrames?: number[];
  onToggleFrame?: (frameNumber: number, frame: InsightFrame) => void;
  onSubmitFrameSelection?: (frames: InsightFrame[], autoRun?: boolean) => void;
  onClearFrameSelection?: () => void;
  onSkipFrameSelection?: () => void;
  onFramesParsed?: (frames: InsightFrame[], reportId: string | undefined) => void;
  customReportsEnabled?: boolean;
  onUpdateCustomReport?: (id: string, updates: Partial<MicroReport>) => void;
  onDeleteCustomReport?: (id: string) => void;
  frameGroups?: FrameCatalogResponse | null;
  onNavigateToReport?: (reportId: string) => void;
  reportSections?: ReportSectionsResponse | null;
  hasFrameBasedReports?: boolean;
  stepStage?: string | null;
  finalRegenerating?: boolean;
  hideMicroreportLinks?: boolean;
  allReports?: MicroReport[];
  workspaceName?: string;
}

const MIN_TOP_HEIGHT = 160;
const MIN_BOTTOM_HEIGHT = 220;
const HANDLE_THICKNESS = 10;

const isCustomReportDebugEnabled = () =>
  typeof window !== 'undefined' && window.localStorage?.getItem('ca_custom_report_debug') === '1';

const isFrameGateDebugEnabled = () =>
  typeof window !== 'undefined' && window.localStorage?.getItem('ca_debug_frame_gate') === '1';

const escapeHtml = (text: string) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const formatDetailLabel = (key: string) =>
  key
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());

const DETAIL_SKIP_KEYS = new Set(['frame_name', 'name', 'title', 'summary', 'source']);

const renderDetailValue = (value: unknown): { html: string; plain: string } => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return { html: trimmed ? escapeHtml(trimmed) : '', plain: trimmed };
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    const text = String(value);
    return { html: escapeHtml(text), plain: text };
  }
  if (Array.isArray(value)) {
    const normalized = value
      .map((entry) => {
        if (typeof entry === 'string') return entry.trim();
        if (typeof entry === 'number' || typeof entry === 'boolean') return String(entry);
        return null;
      })
      .filter((entry): entry is string => Boolean(entry && entry.length));
    if (!normalized.length) return { html: '', plain: '' };
    return {
      html: `<ul>${normalized.map((entry) => `<li>${escapeHtml(entry)}</li>`).join('')}</ul>`,
      plain: normalized[0],
    };
  }
  if (value && typeof value === 'object') {
    const json = JSON.stringify(value);
    return { html: escapeHtml(json), plain: json };
  }
  return { html: '', plain: '' };
};

const convertCardToInsightFrame = (
  card: FrameCardPayload,
  fallbackSource?: string
): InsightFrame => {
  const name = (card.title || '').trim() || `Frame ${card.frame_number}`;
  const initialSummary = (card.summary || '').trim();
  const rawDetails =
    card.details && typeof card.details === 'object' ? (card.details as Record<string, unknown>) : undefined;
  const detailEntries = rawDetails ? Object.entries(rawDetails) : [];
  console.log('[FrameCard] card keys:', Object.keys(card), 'details keys:', rawDetails ? Object.keys(rawDetails) : 'none', 'card.source:', card.source, 'card.summary:', card.summary);
  const detailBlocks: string[] = [];
  let summaryFallback = initialSummary;

  detailEntries.forEach(([key, value]) => {
    if (DETAIL_SKIP_KEYS.has(key.toLowerCase())) return;
    const label = formatDetailLabel(key);
    const { html, plain } = renderDetailValue(value);
    if (!html) return;
    if (!summaryFallback && plain) {
      summaryFallback = plain;
    }
    const block = html.startsWith('<ul>')
      ? `<div><strong>${escapeHtml(label)}:</strong>${html}</div>`
      : `<p><strong>${escapeHtml(label)}:</strong> ${html}</p>`;
    detailBlocks.push(block);
  });

  const descriptionHtml = detailBlocks.join('');
  const resolvedSummary = summaryFallback || name;
  const fullHtmlParts: string[] = [];
  if (initialSummary) {
    fullHtmlParts.push(`<p>${escapeHtml(initialSummary)}</p>`);
  }
  if (descriptionHtml) {
    fullHtmlParts.push(descriptionHtml);
  }
  if (!fullHtmlParts.length) {
    fullHtmlParts.push(`<p>${escapeHtml(resolvedSummary)}</p>`);
  }

  return {
    id: `frame-${card.frame_number}`,
    frameNumber: card.frame_number,
    name,
    summary: resolvedSummary,
    fullHtml: fullHtmlParts.join(''),
    descriptionHtml: descriptionHtml || undefined,
    source: card.source || fallbackSource,
    rawDetails,
  };
};

const convertFrameGroupsToFrames = (
  groups?: FrameCatalogResponse | null
): InsightFrame[] => {
  if (!groups) return [];
  const frames: InsightFrame[] = [];
  (groups.selector ?? []).forEach((card) => {
    frames.push(convertCardToInsightFrame(card, 'frame_selector'));
  });
  (groups.generator ?? []).forEach((card) => {
    frames.push(convertCardToInsightFrame(card, 'frame_generator'));
  });
  return frames.sort((a, b) => a.frameNumber - b.frameNumber);
};

const sanitizeText = (text: string) =>
  text
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`(.*?)`/g, '$1')
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
    .trim();

const wrapLinesAsHtml = (lines: string[]): string => {
  const htmlParts: string[] = [];
  let listItems: string[] = [];

  const flushList = () => {
    if (listItems.length === 0) return;
    htmlParts.push(`<ul>${listItems
      .map((item) => `<li>${sanitizeText(item)}</li>`)
      .join('')}</ul>`);
    listItems = [];
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      return;
    }

    if (/^[-•]/.test(trimmed)) {
      listItems.push(trimmed.replace(/^[-•]\s*/, ''));
    } else {
      flushList();
      htmlParts.push(`<p>${sanitizeText(trimmed)}</p>`);
    }
  });

  flushList();

  return htmlParts.join('');
};

const truncate = (text: string, limit = 140) => {
  if (text.length <= limit) return text;
  return `${text.slice(0, limit - 1)}…`;
};

const sectionHeaderRegex = /^\*\*(.+?)\*\*(?::\s*)?/i;

const buildSections = (lines: string[]) => {
  const sections: Record<string, string[]> = {};
  let current: string | null = null;

  lines.forEach((line) => {
    const headerMatch = line.match(sectionHeaderRegex);
    if (headerMatch) {
      const headerKey = headerMatch[1].replace(/:$/, '').trim().toLowerCase();
      current = headerKey;
      const remainder = line.replace(sectionHeaderRegex, '').trim();
      if (!sections[current]) sections[current] = [];
      if (remainder) sections[current].push(remainder);
      return;
    }

    if (current) {
      if (!sections[current]) sections[current] = [];
      sections[current].push(line);
    }
  });

  return sections;
};

interface FrameListGroup {
  title: string;
  frames: InsightFrame[];
}

interface FrameListDetailProps {
  frames: InsightFrame[];
  groupedFrames?: FrameListGroup[];
  selectedFrames: number[];
  completedFrameSet: Set<number>;
  onToggleFrame?: (frameNumber: number, frame: InsightFrame) => void;
  renderFrameCard: (frame: InsightFrame, isLarge?: boolean) => React.ReactNode;
  isGenerating?: boolean;
}

const FrameCarousel: React.FC<FrameListDetailProps> = ({
  frames,
  groupedFrames,
  selectedFrames,
  completedFrameSet,
  onToggleFrame,
  renderFrameCard,
  isGenerating,
}) => {
  const flatFrames = useMemo(() => {
    if (groupedFrames && groupedFrames.length > 0) {
      return groupedFrames.flatMap((group) => group.frames);
    }
    return frames;
  }, [frames, groupedFrames]);

  const [focusedIndex, setFocusedIndex] = useState(0);
  const focusedIdRef = useRef<string | null>(null);

  const rememberFocus = useCallback(
    (idx: number) => {
      setFocusedIndex(idx);
      focusedIdRef.current = flatFrames[idx]?.id || focusedIdRef.current;
    },
    [flatFrames]
  );

  // When frames change (e.g., select/deselect), restore focus to the same frame if it still exists
  useEffect(() => {
    if (!flatFrames.length) return;
    const currentId = focusedIdRef.current;
    if (currentId) {
      const idx = flatFrames.findIndex((f) => f.id === currentId);
      if (idx !== -1) {
        if (idx !== focusedIndex) rememberFocus(idx);
        return;
      }
    }
    // Fallback: clamp to end
    setFocusedIndex((prev) => Math.max(0, Math.min(prev, flatFrames.length - 1)));
  }, [flatFrames, focusedIndex, rememberFocus]);

  // Also restore focus when selection state changes (IDs stay the same)
  useEffect(() => {
    if (!flatFrames.length) return;
    const currentId = focusedIdRef.current;
    if (!currentId) return;
    const idx = flatFrames.findIndex((f) => f.id === currentId);
    if (idx !== -1 && idx !== focusedIndex) {
      rememberFocus(idx);
    }
  }, [selectedFrames, flatFrames, focusedIndex, rememberFocus]);

  const focusedFrame = flatFrames[focusedIndex] || flatFrames[0];

  const handlePrevious = useCallback(() => {
    setFocusedIndex((prev) => {
      const next = Math.max(prev - 1, 0);
      focusedIdRef.current = flatFrames[next]?.id || focusedIdRef.current;
      return next;
    });
  }, [flatFrames]);

  const handleNext = useCallback(() => {
    setFocusedIndex((prev) => {
      const next = Math.min(prev + 1, flatFrames.length - 1);
      focusedIdRef.current = flatFrames[next]?.id || focusedIdRef.current;
      return next;
    });
  }, [flatFrames]);

  const handleSelectToggle = useCallback(() => {
    if (!focusedFrame) return;
    focusedIdRef.current = focusedFrame.id;
    onToggleFrame?.(focusedFrame.frameNumber, focusedFrame);
  }, [focusedFrame, onToggleFrame]);

  if (flatFrames.length === 0) return null;

  return (
    <div className="frame-split">
      <div className="frame-split-list" role="list">
        {(() => {
          const groups = groupedFrames && groupedFrames.length ? groupedFrames : [{ title: 'Frames', frames }];
          let runningIndex = 0;
          return groups.map((group) => {
            const groupStart = runningIndex;
            runningIndex += group.frames.length;
            return (
              <div key={group.title} className="frame-list-group">
                <div className="frame-list-group-title">{group.title}</div>
                <div className="frame-list-group-items">
                  {group.frames.map((frame, idx) => {
                    const absoluteIndex = groupStart + idx;
                    const isSelected = selectedFrames.includes(frame.frameNumber);
                    const isCompleted = completedFrameSet.has(frame.frameNumber);
                    const isFocused = absoluteIndex === focusedIndex;
                    return (
                      <button
                        key={frame.id}
                        type="button"
                        className={`frame-list-item ${isFocused ? 'frame-list-item-focused' : ''} ${isSelected ? 'frame-list-item-selected' : ''} ${isCompleted ? 'frame-list-item-completed' : ''}`}
                        onClick={() => rememberFocus(absoluteIndex)}
                      >
                        <div className="frame-list-item-top">
                          <span className="frame-list-item-name">{frame.name}</span>
                          <div className="frame-list-item-flags">
                            {isSelected && <span className="frame-list-item-tag">Selected</span>}
                            {isCompleted && <span className="frame-list-item-tag frame-list-item-tag-completed">Completed</span>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          });
        })()}
      </div>

      <div className="frame-split-detail">
        <div className="frame-split-detail-header">
          <div className="frame-split-detail-title">Frame Details</div>
        </div>

        <div className="frame-split-detail-card">{renderFrameCard(focusedFrame, true)}</div>

        <div className="frame-split-detail-actions frame-split-detail-actions--below">
          <button
            type="button"
            className={`frame-detail-btn ${selectedFrames.includes(focusedFrame.frameNumber) ? 'frame-detail-btn-selected' : ''}`}
            onClick={handleSelectToggle}
          >
            {selectedFrames.includes(focusedFrame.frameNumber) ? 'UnSelect Frame' : 'Select frame'}
          </button>
          <div className="frame-detail-stepper">
            <button type="button" onClick={handlePrevious} disabled={focusedIndex === 0}>
              <ChevronLeft size={18} />
            </button>
            <button type="button" onClick={handleNext} disabled={focusedIndex >= frames.length - 1}>
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
        
        {isGenerating && (
          <div className="frame-split-detail-generating">
            <div className="frame-split-detail-generating-indicator">
              <div className="frame-split-detail-generating-spinner" />
            </div>
            <div className="frame-split-detail-generating-text">Queuing up your analysis</div>
          </div>
        )}
      </div>
    </div>
  );
};

const parseFrames = (rawContent: string): InsightFrame[] => {
  if (!rawContent) return [];
  const content = rawContent.replace(/\r\n/g, '\n');
  const frames: InsightFrame[] = [];
  const frameRegex = /(?:^|\n)###\s*Frame\s*(\d+)(?::|-)?\s*(.*)\n([\s\S]*?)(?=\n###\s*Frame\s*\d+|$)/g;
  let match: RegExpExecArray | null;

  while ((match = frameRegex.exec(content)) !== null) {
    const [, frameNumberRaw, titleTail, bodyRaw] = match;
    const frameNumber = parseInt(frameNumberRaw, 10);
    const body = bodyRaw.trim();
    const lines = body.split(/\n/).map((line) => line.trim());
    const sections = buildSections(lines);
    const descriptionLines = sections['description'] || [];
    const firstLine = lines.find((line) => line.length > 0) || '';
    const summary = truncate(
      sanitizeText(firstLine.replace(/^[-•]\s*/, '')),
      120
    );
    const name = sanitizeText(titleTail || `Frame ${frameNumber}`) || `Frame ${frameNumber}`;
    const fullHtml = wrapLinesAsHtml(lines);

    frames.push({
      id: `frame-${frameNumber}`,
      frameNumber,
      name,
      summary,
      fullHtml,
      descriptionHtml: descriptionLines.length > 0 ? wrapLinesAsHtml(descriptionLines) : undefined,
    });
  }

  return frames;
};

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  report,
  onAddToScratchpad,
  isBusy = false,
  analysisQueuePending = false,
  categories = ["Ungrouped"],
  onAddCategory,
  selectedFrames = [],
  completedFrames = [],
  onToggleFrame,
  onSubmitFrameSelection,
  onClearFrameSelection,
  onSkipFrameSelection,
  onFramesParsed,
  customReportsEnabled = false,
  onUpdateCustomReport,
  onDeleteCustomReport,
  frameGroups = null,
  onNavigateToReport,
  reportSections = null,
  hasFrameBasedReports = false,
  stepStage = null,
  finalRegenerating = false,
  hideMicroreportLinks = false,
  allReports = [],
  workspaceName,
}) => {
  const [selectedText, setSelectedText] = useState('');
  const [scratchpadHotkeyActive, setScratchpadHotkeyActive] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null); // inner content (for selection handling)
  const contentScrollRef = useRef<HTMLDivElement>(null); // scroll container
  const scrollPositionsRef = useRef<Record<string, number>>({});
  const lastReportId = report?.id || 'default';
  const [selectedCategory, setSelectedCategory] = useState<string>('Ungrouped');
  const [customTitleDraft, setCustomTitleDraft] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [topHeight, setTopHeight] = useState<number>(320);
  const onFramesParsedRef = useRef<DocumentViewerProps['onFramesParsed'] | null>(null);
  const isCustomReportDebug = isCustomReportDebugEnabled();

  // Table picker
  const [tables, setTables] = useState<CanvasTableMeta[]>([]);
  const [showTableOverlay, setShowTableOverlay] = useState(false);
  const [activeTableId, setActiveTableId] = useState<number | null>(null);
  const [isSavingReport, setIsSavingReport] = useState(false);
  const [isExportingReport, setIsExportingReport] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isExportingWorkspace, setIsExportingWorkspace] = useState(false);
  const [workspaceExportProgress, setWorkspaceExportProgress] = useState<WorkspaceExportProgress | null>(null);
  const [saveFeedback, setSaveFeedback] = useState<'idle' | 'success' | 'error'>('idle');
  const [exportError, setExportError] = useState<string | null>(null);

  const normalizedReportType = (report?.type || '').toLowerCase();
  const isFramingReport = normalizedReportType === 'framing';
  const isFinalReport = normalizedReportType === 'final';
  const reportMap =
    normalizedReportType === 'step' &&
    report?.map &&
    Array.isArray(report.map.stops) &&
    report.map.stops.length > 0
      ? report.map
      : null;
  const selectedCount = selectedFrames.length;
  const isFramingComplete = Boolean(reportSections?.microreports && reportSections.microreports.length > 0);
  const isAwaitingFrameSelection = stepStage === 'awaiting_frame_selection';
  const isGenerationRunning =
    stepStage === 'step_research_auto_running' ||
    stepStage === 'final_report_running' ||
    finalRegenerating;
  const canGenerateWithoutAdditionalFrames = isAwaitingFrameSelection && !hasFrameBasedReports;
  const canGenerateWithAdditionalFrames = selectedCount > 0 && !isGenerationRunning;
  const isAnalysisQueued = isBusy || analysisQueuePending;
  const isFrameGateDebug = isFrameGateDebugEnabled();

  useEffect(() => {
    if (!isFramingReport || !isFrameGateDebug) return;
    // Debug gate for frame-generation CTA enable/disable decisions.
    console.debug('[FrameGateDebug]', {
      stepStage,
      finalRegenerating,
      selectedCount,
      isGenerationRunning,
      isAnalysisQueued,
      hasFrameBasedReports,
      canGenerateWithAdditionalFrames,
      canGenerateWithoutAdditionalFrames,
    });
  }, [
    isFramingReport,
    isFrameGateDebug,
    stepStage,
    finalRegenerating,
    selectedCount,
    isGenerationRunning,
    isAnalysisQueued,
    hasFrameBasedReports,
    canGenerateWithAdditionalFrames,
    canGenerateWithoutAdditionalFrames,
  ]);

  const frameSource = useMemo<FrameSourceState>(() => {
    if (!isFramingReport) {
      return { mode: 'none', frames: [], selector: [], generator: [] };
    }
    const selectorCount = frameGroups?.selector?.length || 0;
    const generatorCount = frameGroups?.generator?.length || 0;
    if (selectorCount + generatorCount > 0) {
      const selectorCards = (frameGroups?.selector ?? []).map((card) =>
        convertCardToInsightFrame(card, 'frame_selector')
      );
      const generatorCards = (frameGroups?.generator ?? []).map((card) =>
        convertCardToInsightFrame(card, 'frame_generator')
      );
      const combined = [...selectorCards, ...generatorCards].sort(
        (a, b) => a.frameNumber - b.frameNumber
      );
      return {
        mode: 'catalog',
        frames: combined,
        selector: selectorCards,
        generator: generatorCards,
      };
    }
    return {
      mode: 'markdown',
      frames: parseFrames(report?.content || ''),
      selector: [],
      generator: [],
    };
  }, [frameGroups, isFramingReport, report?.content]);

  const frames = frameSource.frames;
  const selectorCatalogFrames = frameSource.selector;
  const generatorCatalogFrames = frameSource.generator;
  const usingCatalogFrames = frameSource.mode === 'catalog';

  const completedFrameSet = useMemo(() => new Set(completedFrames), [completedFrames]);

  const { traditionalFrames, specialFrames, remainingFrames } = useMemo(() => {
    if (!isFramingReport) {
      return { traditionalFrames: [], specialFrames: [], remainingFrames: [] };
    }
    if (usingCatalogFrames) {
      return {
        traditionalFrames: selectorCatalogFrames,
        specialFrames: generatorCatalogFrames,
        remainingFrames: [],
      };
    }
    const traditional = frames.filter((frame) => frame.frameNumber >= 1 && frame.frameNumber <= 10);
    const special = frames.filter((frame) => frame.frameNumber >= 11);
    const others = frames.filter((frame) => frame.frameNumber < 1);
    return {
      traditionalFrames: traditional,
      specialFrames: special,
      remainingFrames: others,
    };
  }, [frames, generatorCatalogFrames, isFramingReport, selectorCatalogFrames, usingCatalogFrames]);

  useEffect(() => {
    setSaveFeedback('idle');
    setExportError(null);
  }, [report?.id]);

  // Persist scroll position while streaming updates
  useEffect(() => {
    const el = contentScrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      scrollPositionsRef.current[lastReportId] = el.scrollTop;
    };
    el.addEventListener('scroll', handleScroll);
    return () => {
      el.removeEventListener('scroll', handleScroll);
    };
  }, [lastReportId]);

  // Restore scroll position only when switching reports (report ID changes)
  // NOT when content updates - this prevents scroll jumping during streaming/generation
  useLayoutEffect(() => {
    const el = contentScrollRef.current;
    if (el) {
      const pos = scrollPositionsRef.current[lastReportId] ?? 0;
      el.scrollTop = pos;
    }
  }, [lastReportId]);

  useEffect(() => {
    const isCustom = (report?.type || '').toLowerCase() === 'custom';
    if (!isCustom) {
      setCustomTitleDraft('');
      return;
    }
    setCustomTitleDraft((prev) => {
      const incoming = report?.title || '';
      return incoming === prev ? prev : incoming;
    });
  }, [report?.id, report?.title, report?.type]);

  useEffect(() => {
    onFramesParsedRef.current = onFramesParsed || null;
  }, [onFramesParsed]);

  useEffect(() => {
    if (isFramingReport) {
      onFramesParsedRef.current?.(frames, report?.id);
    }
  }, [frames, isFramingReport, report?.id]);

  useEffect(() => {
    const resizeListener = (event: MouseEvent) => {
      if (!isResizing || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const rawHeight = event.clientY - rect.top - HANDLE_THICKNESS / 2;
      const maxHeight = rect.height - MIN_BOTTOM_HEIGHT;
      const clamped = Math.max(MIN_TOP_HEIGHT, Math.min(maxHeight, rawHeight));
      setTopHeight(clamped);
    };

    const stopResizing = () => setIsResizing(false);

    window.addEventListener('mousemove', resizeListener);
    window.addEventListener('mouseup', stopResizing);

    return () => {
      window.removeEventListener('mousemove', resizeListener);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [isResizing]);

  useEffect(() => {
    if (!containerRef.current) return;
    const height = containerRef.current.getBoundingClientRect().height;
    const initial = Math.max(
      MIN_TOP_HEIGHT,
      Math.min(height - MIN_BOTTOM_HEIGHT, height * 0.45)
    );
    if (!Number.isNaN(initial)) {
      setTopHeight(initial);
    }
  }, [report?.id]);

  useEffect(() => {
    const handleTablesChanged = (e: Event) => {
      const detail = (e as CustomEvent<CanvasTableMeta[]>).detail;
      if (detail) setTables(detail);
    };
    window.addEventListener('canvas-tables-changed', handleTablesChanged as EventListener);
    return () => window.removeEventListener('canvas-tables-changed', handleTablesChanged as EventListener);
  }, []);

  // Initialize from any existing canvas tables on mount
  useEffect(() => {
    setTables(window.__canvasTables || []);
  }, []);

  // Clear selection after successful targeting paste
  useEffect(() => {
    const onComplete = () => {
      setSelectedText('');
      window.getSelection()?.removeAllRanges();
      setShowTableOverlay(false);
    };
    window.addEventListener('canvas-targeting-complete', onComplete as EventListener);
    return () => window.removeEventListener('canvas-targeting-complete', onComplete as EventListener);
  }, []);

  useEffect(() => {
    const isTypingTarget = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName.toLowerCase();
      return target.isContentEditable || tag === 'input' || tag === 'textarea' || tag === 'select';
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 's' && !isTypingTarget(event.target)) {
        setScratchpadHotkeyActive(true);
      }
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 's') {
        setScratchpadHotkeyActive(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const extractSelectionHtml = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return '';
    }
    const container = document.createElement('div');
    for (let index = 0; index < selection.rangeCount; index += 1) {
      const range = selection.getRangeAt(index);
      container.appendChild(range.cloneContents());
    }
    return container.innerHTML;
  };

  const copySelectionToClipboard = useCallback(async (plainText: string, htmlText: string) => {
    if (!plainText && !htmlText) {
      return;
    }
    try {
      const canWriteRichContent =
        typeof ClipboardItem !== 'undefined' &&
        Boolean(navigator.clipboard) &&
        typeof navigator.clipboard.write === 'function';

      if (canWriteRichContent) {
        const clipboardItems: Record<string, Blob | Promise<Blob>> = {};
        if (plainText) {
          clipboardItems['text/plain'] = new Blob([plainText], { type: 'text/plain' });
        }
        if (htmlText) {
          clipboardItems['text/html'] = new Blob([htmlText], { type: 'text/html' });
        }
        await navigator.clipboard.write([new ClipboardItem(clipboardItems)]);
        return;
      }

      if (navigator.clipboard?.writeText && plainText) {
        await navigator.clipboard.writeText(plainText);
        return;
      }

      if (plainText) {
        const textarea = document.createElement('textarea');
        textarea.value = plainText;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
    } catch (error) {
      console.error('Copy failed', error);
    }
  }, []);

  const handleMouseUp = async () => {
    const selection = window.getSelection();
    const text = selection?.toString().trim() || '';
    if (!text) {
      return;
    }
    const html = extractSelectionHtml();
    await copySelectionToClipboard(text, html);
    if (scratchpadHotkeyActive) {
      setSelectedText(text);
    } else {
      setSelectedText('');
      setShowTableOverlay(false);
      window.getSelection()?.removeAllRanges();
    }
  };

  const addSelectionToScratchpad = () => {
    if (selectedText) {
      onAddToScratchpad({
        type: 'text',
        content: selectedText,
        source: report?.title || 'Unknown'
      }, selectedCategory);
      setSelectedText('');
      window.getSelection()?.removeAllRanges();
    }
  };

  // Start targeting mode to click a canvas table cell and paste selection
  const startTargeting = () => {
    if (!selectedText) return;
    window.dispatchEvent(new CustomEvent('canvas-targeting-start', { detail: { content: selectedText } }));
  };

  const openTableOverlay = () => {
    const available = window.__canvasTables || [];
    setTables(available);
    setActiveTableId(available.length ? available[0].id : null);
    setShowTableOverlay(true);
  };

  const sendToTable = (nodeId: number, row: number, col: number) => {
    if (!selectedText) return;
    window.dispatchEvent(new CustomEvent('canvas-table-fill', { detail: { nodeId, row, col, content: selectedText } }));
    setShowTableOverlay(false);
    setSelectedText('');
    window.getSelection()?.removeAllRanges();
  };

  const handleSaveReport = async () => {
    if (!report?.id) return;
    const markdown = (report.content || '').trim();
    if (!markdown) {
      setSaveFeedback('error');
      return;
    }
    setIsSavingReport(true);
    setSaveFeedback('idle');
    try {
      await apiService.saveReportExport(report.id, report.content || '', {
        title: report.title,
        subtitle: report.date ? `Updated ${report.date}` : undefined,
      });
      setSaveFeedback('success');
      setTimeout(() => setSaveFeedback('idle'), 2500);
    } catch (error) {
      console.error(error);
      setSaveFeedback('error');
    } finally {
      setIsSavingReport(false);
    }
  };

  const handleExportDocx = async () => {
    if (!report?.id) return;
    const markdown = (report.content || '').trim();
    if (!markdown) {
      setExportError('Analysis is empty');
      return;
    }
    setIsExportingReport(true);
    setExportError(null);
    try {
      const blob = await apiService.exportReportDocx(report.id, report.content || '', {
        title: report.title,
        subtitle: report.date ? `Updated ${report.date}` : undefined,
        is_custom: customEditingEnabled,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${report.title || 'analysis'}.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error(error);
      setExportError(error?.message || 'Failed to export analysis');
    } finally {
      setIsExportingReport(false);
    }
  };

  const handleExportPdf = async () => {
    if (!report?.id) return;

    const node = contentRef.current?.querySelector('.interactive-report') as HTMLElement | null;
    const html = node?.outerHTML || '';
    if (!html.trim()) {
      setExportError('Nothing to export');
      return;
    }

    setIsExportingPdf(true);
    setExportError(null);
    try {
      const blob = await apiService.exportReportPdf(report.id, html, { title: report.title || 'analysis', is_custom: customEditingEnabled });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${report.title || 'analysis'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error(error);
      setExportError(error?.message || 'Failed to export PDF');
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleExportWorkspace = async () => {
    if (isExportingWorkspace) return;
    setIsExportingWorkspace(true);
    setExportError(null);
    try {
      await exportWorkspacePdfZip(allReports, {
        reportSections,
        hideMicroreportLinks,
        workspaceName,
        onProgress: setWorkspaceExportProgress,
      });
    } catch (error: any) {
      console.error(error);
      setExportError(error?.message || 'Failed to export workspace');
    } finally {
      setIsExportingWorkspace(false);
      setWorkspaceExportProgress(null);
    }
  };

  const handleCustomReportContentChange = useCallback(
    (next: string) => {
      if (!report?.id) return;
      if (isCustomReportDebug) {
        // eslint-disable-next-line no-console
        console.log('[CustomReportDebug] DocumentViewer -> onUpdateCustomReport', {
          id: report.id,
          length: next.length,
          snippet: next.slice(0, 120),
        });
      }
      onUpdateCustomReport?.(report.id, { content: next });
    },
    [isCustomReportDebug, onUpdateCustomReport, report?.id]
  );

  if (!report) {
    return (
      <div className="flex-1 bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400 text-lg">Select a document to preview</p>
        </div>
      </div>
    );
  }


  const isCustomReport = normalizedReportType === 'custom';
  const customEditingEnabled = customReportsEnabled && isCustomReport;

  const formatReportTitle = (title: string | undefined | null) => {
    if (!title || title.trim().length === 0) return 'Untitled Analysis';
    const stepMatch = title.match(/^Step\s+(\d+)\s*:\s*(.*)$/i);
    if (stepMatch) {
      const [, stepNumber, remainder] = stepMatch;
      const trimmedRemainder = remainder.trim();
      const microLabel = `Microanalysis ${stepNumber}`;
      return trimmedRemainder ? `${microLabel} : ${trimmedRemainder}` : microLabel;
    }
    return title;
  };

  const displayTitle =
    normalizedReportType === 'early_insights'
      ? 'Quick Industry Trends'
      : normalizedReportType === 'framing'
        ? 'Choose additional analysis Frames from here'
        : formatReportTitle(report?.title);
  const headingSize = normalizedReportType === 'early_insights' ? 'text-2xl' : 'text-lg';
  const interactiveReportTitleOverride =
    isFramingReport && !usingCatalogFrames ? 'Additional Competitive Frames for consideration' : undefined;
  const showDocumentBody = !(isFramingReport && usingCatalogFrames);

  const renderFrameCard = (frame: InsightFrame, isLarge: boolean = false) => {
    const isSelected = selectedFrames.includes(frame.frameNumber);
    const isCompleted = completedFrameSet.has(frame.frameNumber);
    const isInteractive = !isCompleted && !(isSelected && (isGenerationRunning || isAnalysisQueued));
    console.log('[FrameInteractive]', { frameNumber: frame.frameNumber, isSelected, isCompleted, isInteractive, isGenerationRunning, isAnalysisQueued, selectedFrames });
    const details = frame.rawDetails as Record<string, any> | undefined;
    const lens = details?.lens || details?.angle || frame.summary || '';
    const analysis = details?.analysis || details?.use || '';
    const insights = details?.insights || details?.unique_insight || '';
    return (
      <div
        key={frame.id}
        className={`frame-card ${isLarge ? 'frame-card-large' : 'frame-card-small'} ${isSelected ? 'frame-card-selected' : ''} ${isCompleted ? 'frame-card-disabled' : ''}`}
        role="button"
        aria-pressed={isSelected}
        aria-disabled={isCompleted}
        tabIndex={isInteractive ? 0 : -1}
        onClick={() => {
          if (isInteractive) onToggleFrame?.(frame.frameNumber, frame);
        }}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && isInteractive) {
            e.preventDefault();
            onToggleFrame?.(frame.frameNumber, frame);
          }
        }}
      >
        {isSelected && (
          <div className="frame-card-check">
            <CheckCircle2 className="frame-card-check-icon" />
          </div>
        )}
        {isCompleted && (
          <div className="frame-card-badge frame-card-badge-completed">Completed</div>
        )}
        <div className="frame-card-label">
          <div className="frame-card-number">{frame.frameNumber}</div>
          <span className="frame-card-title">{frame.name}</span>
        </div>
        {lens && <div className="frame-card-subtitle">{lens}</div>}
        {isLarge ? (
          <div className="frame-card-description frame-card-description--rich">
            {analysis && (
              <div className="frame-card-section">
                <div className="frame-card-section-title">
                  <Search size={14} /> <span>Analysis</span>
                </div>
                <p>{analysis}</p>
              </div>
            )}
            {insights && (
              <div className="frame-card-section">
                <div className="frame-card-section-title">
                  <Sparkles size={14} /> <span>Insight</span>
                </div>
                <p>{insights}</p>
              </div>
            )}
            {!analysis && !insights && (
              <div
                className="frame-card-section"
                dangerouslySetInnerHTML={{
                  __html:
                    frame.fullHtml ||
                    frame.descriptionHtml ||
                    `<p>${escapeHtml(frame.summary || '')}</p>`,
                }}
              />
            )}
          </div>
        ) : (
          <div
            className="frame-card-description"
            dangerouslySetInnerHTML={{
              __html: frame.descriptionHtml || `<p>${escapeHtml(frame.summary || '')}</p>`,
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className="document-viewer-panel flex-1 min-w-0 bg-gradient-to-br from-slate-900 to-slate-800 flex flex-col overflow-hidden"
    >
      {/* Document Header */}
      <div className="document-viewer-header px-6 py-4 mt-2 border-b border-slate-700/50 flex items-center justify-between">
        <div className="document-heading">
          {customEditingEnabled ? (
            <input
              className="custom-report-title-input"
              value={customTitleDraft}
              placeholder="Untitled custom analysis"
              onChange={(event) => {
                const nextValue = event.target.value;
                setCustomTitleDraft(nextValue);
                if (nextValue === report.title) return;
                if (isCustomReportDebug) {
                  // eslint-disable-next-line no-console
                  console.log('[CustomReportDebug] DocumentViewer title change', {
                    id: report.id,
                    length: nextValue.length,
                    snippet: nextValue.slice(0, 60),
                  });
                }
                onUpdateCustomReport?.(report.id, { title: nextValue });
              }}
            />
          ) : (
            <h2 className={`${headingSize} font-semibold text-white`}>{displayTitle}</h2>
          )}
        </div>
        {!isFramingReport && (
          <div className="document-action-bar">
            {customEditingEnabled && (
              <button
                type="button"
                className="document-action-button document-action-button--danger"
                style={{ color: '#ffffff' }}
                onClick={() => {
                  if (!report?.id) return;
                  const confirmed = window.confirm('Delete this custom analysis? This cannot be undone.');
                  if (confirmed) {
                    onDeleteCustomReport?.(report.id);
                  }
                }}
              >
                Delete
              </button>
            )}
            {/* Save button hidden — function exists in dormant state (see KNOWN_ISSUES.md) */}
            {false && customEditingEnabled && (
              <button
                type="button"
                onClick={handleSaveReport}
                disabled={isSavingReport || isBusy}
                className="document-action-button document-action-button--ghost"
                style={{ color: '#f8fafc' }}
              >
                {isSavingReport ? 'Saving…' : 'Save'}
              </button>
            )}
            <button
              type="button"
              onClick={handleExportDocx}
              disabled={isExportingReport || isBusy}
              className="document-action-button"
              style={{ color: '#f8fafc' }}
            >
              {isExportingReport ? 'Preparing…' : 'Export DOCX'}
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={isExportingPdf || isBusy}
              className="document-action-button"
              style={{ color: '#f8fafc' }}
            >
              {isExportingPdf ? 'Preparing…' : 'Export PDF'}
            </button>
            {getExportableReports(allReports).length > 1 && (
              <button
                type="button"
                onClick={handleExportWorkspace}
                disabled={isExportingWorkspace || isBusy}
                className="document-action-button"
                style={{ color: '#f8fafc' }}
                title="Download every analysis in this workspace (Scope, Quick Trends, microanalyses, and final) as one zip of PDFs"
              >
                {isExportingWorkspace
                  ? `Exporting ${workspaceExportProgress ? `${workspaceExportProgress.current}/${workspaceExportProgress.total}` : ''}…`
                  : 'Export Workspace'}
              </button>
            )}
            {/* Save feedback hidden — dormant (see KNOWN_ISSUES.md) */}
            {exportError && (
              <span className="document-action-feedback document-action-feedback--error">{exportError}</span>
            )}
          </div>
        )}
      </div>

      {/* Frame selection view */}
      {isFramingReport && (
      <div className="frame-gallery" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {frames.length > 0 ? (
          <div className="frame-split-shell" style={{ flex: 1, minHeight: 0 }}>
            <FrameCarousel
              frames={frames}
              groupedFrames={[
                { title: 'Traditional Analysis Frames list specially curated for you', frames: traditionalFrames },
                {
                  title: 'Special Frames list based on your most recent industries trends',
                  frames: [...specialFrames, ...remainingFrames],
                },
              ].filter((group) => group.frames.length > 0)}
              selectedFrames={selectedFrames}
              completedFrameSet={completedFrameSet}
              onToggleFrame={onToggleFrame}
              renderFrameCard={renderFrameCard}
              isGenerating={isAnalysisQueued}
            />
          </div>
        ) : (
          <div className="frame-placeholder">
            <div className="frame-placeholder-icon">#</div>
            <div className="frame-placeholder-title">Insight frames will appear here</div>
            <div className="frame-placeholder-copy">Generate a framing analysis to visualize its strategic frames as rich cards.</div>
          </div>
        )}
        <div className="frame-gallery-toolbar frame-gallery-toolbar--bottom">
          <div className="frame-gallery-toolbar-right">
            <span className="frame-gallery-selection-count">{selectedCount} selected</span>
            <div className="frame-gallery-actions">
              {selectedCount > 0 && (
                <button
                  type="button"
                  className="frame-gallery-clear"
                  onClick={() => onClearFrameSelection?.()}
                >
                  Clear
                </button>
              )}
              <button
                type="button"
                className="frame-gallery-submit"
                disabled={!canGenerateWithAdditionalFrames || isResizing || isAnalysisQueued || isGenerationRunning}
                onClick={() => {
                  console.log('[FrameSelection] Button clicked', { selectedFrames, framesCount: frames.length });
                  const chosen = frames.filter((frame) => selectedFrames.includes(frame.frameNumber));
                  console.log('[FrameSelection] Chosen frames:', chosen);
                  if (chosen.length > 0) {
                    console.log('[FrameSelection] Calling onSubmitFrameSelection');
                    onSubmitFrameSelection?.(chosen, true);
                  }
                }}
              >
                Generate Analyses with additional frames
              </button>
              {!hasFrameBasedReports && (
                <button
                  type="button"
                  className="frame-gallery-skip"
                  disabled={selectedCount > 0 || isResizing || isAnalysisQueued || isGenerationRunning || !canGenerateWithoutAdditionalFrames}
                  onClick={() => onSkipFrameSelection?.()}
                  title={canGenerateWithoutAdditionalFrames ? "Generate analyses without selecting additional frames" : isFramingComplete ? "Frame selection is no longer available for this workspace" : "Analysis setup is still in progress..."}
                >
                  Generate Analysis without additional frames
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Resize Handle: hidden for Competitive Frames, shown for other reports */}
      {isFramingReport ? null : (
      <div
        className={`horizontal-resize-handle ${isResizing ? 'horizontal-resize-handle-active' : ''}`}
        onMouseDown={(event) => {
          event.preventDefault();
          setIsResizing(true);
        }}
      />
      )}

      {/* Document Content: hidden for Competitive Frames, shown for other reports */}
      {!isFramingReport && (
      <div className="relative flex-1 min-h-0 flex flex-col overflow-hidden">
      <div ref={contentScrollRef} className="document-viewer-content flex-1 overflow-y-auto p-3 relative">
        {customEditingEnabled ? (
          <div ref={contentRef}>
            <CustomReportEditor
              value={report.content || ''}
              onChange={handleCustomReportContentChange}
            />
          </div>
        ) : showDocumentBody ? (
          <div
            ref={contentRef}
            className="report-shell mx-auto text-gray-900 relative"
            onMouseUp={handleMouseUp}
          >
            <ReportErrorBoundary reportId={report.id}>
              <InteractiveReport
                markdown={report.content || ''}
                titleOverride={interactiveReportTitleOverride}
                onNavigateToReport={onNavigateToReport}
                reportSections={reportSections}
                isFinalReport={isFinalReport}
                hideMicroreportLinks={hideMicroreportLinks}
              />
            </ReportErrorBoundary>
            {isBusy && (
              <div className="absolute inset-0 bg-black/10 flex items-center justify-center">
                <div className="bg-indigo-900/70 text-white px-4 py-2 rounded-full text-sm shadow-lg">Generating document…</div>
              </div>
            )}
          </div>
        ) : (
          <div className="report-shell mx-auto text-gray-300 text-center py-20">
            <p className="text-xl font-semibold mb-3">Interactive cards replaced the framing document.</p>
            <p className="text-base text-gray-400 max-w-2xl mx-auto">
              Review the cards above to choose frames for your research. Switch to the Quick Industry Trends report to read the generated narrative and sources.
            </p>
          </div>
        )}

        {/* Selection Popup */}
        {selectedText && !customEditingEnabled && (
          <div
            className="fixed bottom-8 left-1/2 z-50 w-[min(96vw,900px)] -translate-x-1/2 rounded-2xl p-4 text-slate-50 animate-fade-in"
            style={{
              border: '1px solid rgba(255, 255, 255, 0.34)',
              backgroundColor: 'rgba(15, 23, 42, 0.44)',
              backgroundImage: 'linear-gradient(135deg, rgba(129, 140, 248, 0.22), rgba(15, 23, 42, 0.38), rgba(103, 232, 249, 0.2))',
              boxShadow:
                '0 26px 70px -24px rgba(2, 6, 23, 0.85), inset 0 1px 0 rgba(255, 255, 255, 0.35), inset 0 -1px 0 rgba(148, 163, 184, 0.2)',
              backdropFilter: 'blur(24px) saturate(175%)',
              WebkitBackdropFilter: 'blur(24px) saturate(175%)',
            }}
          >
            <div className="mb-2 flex" style={{ justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setSelectedText(''); window.getSelection()?.removeAllRanges(); setShowTableOverlay(false); }}
                className="rounded px-2 py-1 text-xs text-indigo-200 transition-colors hover:bg-slate-800 hover:text-white"
                title="Close"
              >
                x
              </button>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-[1.35fr_1.2fr_auto] md:items-end md:gap-x-16 md:gap-y-6">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-indigo-100/90">Select Group</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value !== '__add_new_category__') {
                      setSelectedCategory(value);
                      return;
                    }
                    const created = window.prompt('Enter new group name');
                    const trimmed = (created || '').trim();
                    if (!trimmed) return;

                    const existing = categories.find((category) => category.toLowerCase() === trimmed.toLowerCase());
                    if (existing) {
                      setSelectedCategory(existing);
                      return;
                    }
                    onAddCategory?.(trimmed);
                    setSelectedCategory(trimmed);
                  }}
                  className="w-full appearance-none rounded-lg border border-indigo-100/55 bg-white/10 px-3 py-2 text-sm text-slate-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] outline-none transition-colors hover:border-indigo-100/70 focus:border-cyan-100/80"
                  style={{
                    backgroundColor: 'rgba(15, 23, 42, 0.72)',
                    backdropFilter: 'blur(10px) saturate(140%)',
                    WebkitBackdropFilter: 'blur(10px) saturate(140%)',
                    color: '#e2e8f0',
                    WebkitTextFillColor: '#e2e8f0',
                    WebkitAppearance: 'none',
                    appearance: 'none',
                  }}
                >
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                  <option value="__add_new_category__">+ Add new group...</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-indigo-100/90">Choose Table</label>
                <select
                  value={activeTableId ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value) {
                      setActiveTableId(null);
                      setShowTableOverlay(false);
                      return;
                    }
                    const nextId = Number(value);
                    setActiveTableId(nextId);
                    setShowTableOverlay(true);
                  }}
                  className="w-full appearance-none rounded-lg border border-indigo-100/55 bg-white/10 px-3 py-2 text-sm text-slate-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] outline-none transition-colors hover:border-indigo-100/70 focus:border-cyan-100/80 disabled:cursor-not-allowed disabled:opacity-60"
                  style={{
                    backgroundColor: 'rgba(15, 23, 42, 0.72)',
                    backdropFilter: 'blur(10px) saturate(140%)',
                    WebkitBackdropFilter: 'blur(10px) saturate(140%)',
                    color: '#e2e8f0',
                    WebkitTextFillColor: '#e2e8f0',
                    WebkitAppearance: 'none',
                    appearance: 'none',
                  }}
                  disabled={tables.length === 0}
                >
                  <option value="">{tables.length === 0 ? 'No tables available' : 'Select table...'}</option>
                  {tables.map((table) => (
                    <option key={table.id} value={table.id}>{table.label}</option>
                  ))}
                </select>
              </div>

              <div className="col-span-1 mt-4 flex w-full justify-end md:col-span-3 md:mt-4">
                <button
                  onClick={addSelectionToScratchpad}
                  className="h-[38px] rounded-lg px-4 text-sm font-semibold text-white shadow-lg transition-all hover:-translate-y-px"
                  style={{ backgroundColor: '#2563eb', border: '1px solid rgba(191, 219, 254, 0.9)' }}
                >
                  Add to Scrachpad
                </button>
              </div>
            </div>
          </div>
        )}

        {showTableOverlay && activeTableId !== null && createPortal(
          <div className="fixed inset-0 flex items-center justify-center bg-black/60" style={{ zIndex: 2001 }} onClick={() => setShowTableOverlay(false)}>
            <div className="bg-slate-900 border border-indigo-700/50 rounded-xl shadow-2xl p-4 max-w-[90vw] max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
              <div className="mb-3 flex items-center gap-2">
                <span className="text-sm text-indigo-200">Table:</span>
                <select
                  className="bg-slate-800 text-white border border-indigo-700/50 rounded px-2 py-1 text-sm"
                  value={activeTableId}
                  onChange={(e) => setActiveTableId(Number(e.target.value))}
                >
                  {(tables || []).map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
                <button className="ml-auto text-indigo-300 hover:text-white text-sm" onClick={() => setShowTableOverlay(false)}>Close</button>
              </div>
              {tables.find((t) => t.id === activeTableId) && (
                <div className="overflow-auto">
                  <table className="border-collapse">
                    <tbody>
                      {Array.from({ length: tables.find((t) => t.id === activeTableId)!.rows }).map((_, r) => (
                        <tr key={r}>
                          {Array.from({ length: tables.find((t) => t.id === activeTableId)!.cols }).map((_, c) => {
                            const t = tables.find((tb) => tb.id === activeTableId)!;
                            const isHeader = r === 0 || c === 0;
                            const display = r === 0 ? (t.colTitles[c] || '') : c === 0 ? (t.rowTitles[r] || '') : '';
                            return (
                              <td key={c} className={`border border-indigo-700/50 ${isHeader ? 'bg-indigo-900/40' : 'bg-indigo-800/30'} p-3 min-w-[80px] text-indigo-100`}>
                                <div
                                  className={`text-xs ${isHeader ? 'opacity-90' : 'opacity-70 hover:opacity-100 cursor-pointer'}`}
                                  onClick={() => { if (!isHeader) sendToTable(t.id, r, c); }}
                                  title={isHeader ? '' : `Insert to R${r+1}, C${c+1}`}
                                >
                                  {isHeader ? display : 'Click to paste'}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
      </div>

      {/* Interpretive map front layer (only for step reports that shipped a map) */}
      {reportMap && (
        <MapView key={report.id} map={reportMap} scrollContainerRef={contentScrollRef} />
      )}
      </div>
      )}

    </div>
  );
};

export default React.memo(DocumentViewer);

